import { CloudUpload, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/constants';
import { getBackendErrorCode } from '@/helpers';

import { logEvent } from '../../../helpers/eventLogger';

// Codes for which refreshing is pointless (the file is gone) — offer a fresh
// upload instead.
const FILE_GONE_CODES = ['FILE_IS_DELETED', 'NO_ATTACHMENT_FOUND'];

interface ApplicationErrorProps {
  error?: unknown;
}

/**
 * The whole-app failure screen.
 *
 * It renders precisely when things are broken — including as the top-level ErrorBoundary
 * fallback, above `<BrowserRouter>` and outside every provider. Its old stylesheet had to
 * hardcode a hex because it could not count on a ThemeProvider being above it; Tailwind
 * utilities and the tokens in `globals.css` have no such dependency, so that caveat is gone.
 */
const ApplicationError = ({ error }: ApplicationErrorProps) => {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.global.applicationError' });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // When the failure carries a known backend error code (e.g. FILE_IS_DELETED),
  // show its translated message instead of the generic title. Falls back to the
  // generic title for plain JS errors (e.g. the top-level ErrorBoundary).
  const backendCode = getBackendErrorCode(error);
  const title = backendCode && i18n.exists(`back:${backendCode}`) ? i18n.t(`back:${backendCode}`) : t('title');
  const isFileGone = FILE_GONE_CODES.includes(backendCode);

  useEffect(() => {
    logEvent({
      logType: 'ERROR',
      eventType: 'PROCESS_FILE_ERROR',
      trace: error instanceof Error ? error.stack || error.message : String(error),
    });
  }, [error]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  // Full navigation (not react-router) so this works even when ApplicationError
  // renders as the top-level ErrorBoundary fallback, outside <BrowserRouter>.
  const handleUploadNew = () => {
    window.location.assign(ROUTES.home(i18n.language));
  };

  return (
    <div className="flex min-h-[500px] flex-col items-center justify-center gap-10 px-10 py-12 md:flex-row md:gap-16">
      <div className="flex flex-col items-start gap-[30px]">
        <h1 className="max-w-[600px] text-h3">{title}</h1>
        {isFileGone ? (
          <Button variant="ink" size="lg" onClick={handleUploadNew}>
            <CloudUpload size={16} />
            {t('uploadNew')}
          </Button>
        ) : (
          <Button variant="ink" size="lg" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw size={16} />
            {isRefreshing ? t('refreshing') : t('refresh')}
          </Button>
        )}
      </div>

      {/* Masked rather than an <img> so the line art inherits `--foreground`. */}
      <div
        aria-hidden
        className="size-[240px] shrink-0 bg-foreground"
        style={{
          maskImage: 'url("/assets/images/error.png")',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
        }}
      />
    </div>
  );
};

export default ApplicationError;
